import { useCallback, useEffect, useState } from "react";
import { pushSubscriptions } from "../lib/api";

const DEFAULT_VAPID_PUBLIC_KEY = "BAmnXbAEmPovsc_lq9khcEiosIp9nl_Xn9PjJKtYjVJmFjPfs7boMU76Y3JrvNkK4JYerXCezevLRll3fPyx-3c";

function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
    const padding = "=".repeat((4 - value.length % 4) % 4);
    const binary = atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(new ArrayBuffer(binary.length));
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

export type WebPushState = "unsupported" | "idle" | "subscribed" | "denied" | "working" | "error";

async function registerSubscription(subscription: PushSubscription) {
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error("Push subscription keys unavailable");
    await pushSubscriptions.register({ endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth, userAgent: navigator.userAgent });
}

export function useWebPush(enabled: boolean) {
    const supported = typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager" in window
        && "Notification" in window;
    const [error, setError] = useState<string | null>(null);
    const [state, setState] = useState<WebPushState>(supported ? "idle" : "unsupported");

    useEffect(() => {
        if (!enabled || !supported) return;
        if (Notification.permission === "denied") { setState("denied"); return; }
        let cancelled = false;
        void navigator.serviceWorker.register("/sw.js")
            .then(() => navigator.serviceWorker.ready)
            .then(registration => registration.pushManager.getSubscription())
            .then(async subscription => {
                if (subscription) await registerSubscription(subscription);
                if (!cancelled) setState(subscription ? "subscribed" : "idle");
            })
            .catch(() => { if (!cancelled) setState("error"); });
        return () => { cancelled = true; };
    }, [enabled, supported]);

    const subscribe = useCallback(async () => {
        if (!enabled || !supported) return false;
        setError(null);
        setState("working");
        try {
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                setState(permission === "denied" ? "denied" : "idle");
                return false;
            }
            await navigator.serviceWorker.register("/sw.js");
            const registration = await navigator.serviceWorker.ready;
            const existing = await registration.pushManager.getSubscription();
            const subscription = existing ?? await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: applicationServerKey(
                    (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? DEFAULT_VAPID_PUBLIC_KEY,
                ),
            });
            await registerSubscription(subscription);
            setState("subscribed");
            return true;
        } catch (cause) {
            const failure = cause as { name?: string; message?: string; status?: number; body?: { detail?: string } };
            setError(failure.status === 403
                ? "The server refused browser registration for this account (403)."
                : failure.name === "NotAllowedError"
                    ? "Notifications are blocked. Allow them in your browser’s site settings."
                    : failure.body?.detail ?? failure.message ?? "Browser registration failed. Please retry.");
            setState("error");
            return false;
        }
    }, [enabled, supported]);

    return { supported, state, error, subscribe };
}
